'use client';

import clsx from 'clsx';
import { useMemo, type ForwardedRef, type ReactNode } from 'react';
import {
  DisclosureGroup as RACDisclosureGroup,
  type DisclosureGroupProps as RACDisclosureGroupProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { CommonProps, Key } from '../types.js';
import {
  DisclosureContext,
  type DisclosureContextValue,
  type DisclosureSize,
  type DisclosureVariant,
} from './context.js';

export interface DisclosureGroupProps
  extends
    CommonProps,
    Pick<RACDisclosureGroupProps, 'isDisabled' | 'allowsMultipleExpanded' | 'defaultExpandedKeys' | 'expandedKeys'> {
  /** The disclosures to group together. */
  children: ReactNode;
  /**
   * Determines the visual appearance of disclosures within this group.
   * @default 'base'
   */
  variant?: DisclosureVariant;
  /**
   * Determines the size of disclosures within this group.
   * @default 'medium'
   */
  size?: DisclosureSize;
  /** Handler that is called when any disclosure in this group is expanded or collapsed. */
  onExpandedChange?: (keys: Set<Key>) => void;
}

function DisclosureGroup(
  { children, variant = 'base', size = 'medium', UNSAFE_className, UNSAFE_style, ...props }: DisclosureGroupProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const disclosureContext: DisclosureContextValue = useMemo(
    () => ({
      variant,
      size,
    }),
    [variant, size],
  );

  return (
    <RACDisclosureGroup
      {...props}
      ref={ref}
      className={clsx('cim-disclosure-group', UNSAFE_className)}
      style={UNSAFE_style}
      data-variant={variant}
      data-size={size}
    >
      <DisclosureContext.Provider value={disclosureContext}>{children}</DisclosureContext.Provider>
    </RACDisclosureGroup>
  );
}

/**
 * Displays a grouping of related disclosures.
 *
 * See [disclosure usage guidelines](https://ui.cimpress.io/components/disclosure/).
 */
const _DisclosureGroup = withStyleProps(forwardRef(DisclosureGroup), 'DisclosureGroup');

export { _DisclosureGroup as DisclosureGroup };
